import { Body } from "../../../../components/main-body/body";
import { useCallback, useMemo, useState, useEffect } from "react";
import moment from "moment-timezone";
import dayjs from "dayjs";
import { DatePicker, Divider } from "antd";
import WeekPlan from "./week-plan";
import AssignTaskDrawer from "./assign-task-drawer";
import AssignedTaskDrawer from "./assigned-task-drawer";
import UpcomingAssignments from "./upcoming-assignments";
import ModalWrapper from "../../../../components/modal/modal";
import DropdownFilters from "../../../../components/dropdown-filters/dropdown-filters";
import ApiTaskManagement from "../../../../api/task-managements";
import { useCan } from "../../../../common/utils/usePermissions";

const Plan = ({ subjectId, startedAt, timezone }) => {
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [task, setTask] = useState();
  const [filters, setFilters] = useState({});
  const [taskList, setTaskList] = useState();
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  // LF360-2024: the server checks the same permissions.
  const canAssign = useCan("plan.manage");
  const canSeeUpcoming = useCan("plan.upcoming");
  // Bumped after assign/remove to force WeekPlan and UpcomingAssignments to
  // refetch — both depend on this counter via key / effect dep.
  const [refreshTick, setRefreshTick] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // ---- Future-tasks preview (read-only; nothing is scheduled) ----
  // NOTE: antd's DatePicker here is dayjs-based, so previewMonth is a dayjs
  // value (using moment broke the picker). Week math still uses moment-timezone.
  // Future-tasks preview opens in a (wide) modal — see ModalWrapper below.
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewMonth, setPreviewMonth] = useState(() =>
    dayjs().add(1, "month").startOf("month")
  );

  // Cap the month picker to [current month .. +12 months].
  const disabledPreviewMonth = (current) => {
    if (!current) return false;
    const min = dayjs().startOf("month");
    const max = dayjs().add(12, "months").endOf("month");
    return current.isBefore(min, "month") || current.isAfter(max, "month");
  };

  // Sunday-start weeks overlapping the selected month. The first/last week may
  // spill into adjacent months — that's expected. Each week's header shows the
  // REAL study week number (weeks since the subject's first week). Past weeks
  // are dropped (current month shows only weeks that haven't fully passed).
  // Ordered later-on-top to match the main list (newest first).
  const previewWeeks = useMemo(() => {
    if (!previewModalOpen || !previewMonth || !timezone) return [];
    const startOfFirstWeek =
      startedAt ? moment.tz(startedAt, timezone).startOf("week") : null;
    const ym = previewMonth.format("YYYY-MM");
    const monthStart = moment.tz(`${ym}-01`, "YYYY-MM-DD", timezone).startOf("month");
    const monthEnd = monthStart.clone().endOf("month");
    const todayStart = moment.tz(timezone).startOf("day");

    const out = [];
    let weekStart = monthStart.clone().startOf("week");
    while (weekStart.isSameOrBefore(monthEnd, "day")) {
      const weekEnd = weekStart.clone().endOf("week");
      // Only future weeks (a week still ongoing today counts as future).
      if (weekEnd.isSameOrAfter(todayStart)) {
        const realWeekIndex = startOfFirstWeek
          ? Math.round(weekStart.clone().diff(startOfFirstWeek, "weeks", true))
          : 0;
        out.push({
          key: weekStart.format("YYYY-MM-DD"),
          startDate: weekStart.toISOString(),
          endDate: weekEnd.toISOString(),
          weekIndex: realWeekIndex,
        });
      }
      weekStart = weekStart.clone().add(1, "week");
    }
    // later weeks on top, nearer (earlier) weeks below
    return out.reverse();
  }, [previewModalOpen, previewMonth, timezone, startedAt]);

  const updateFilterValue = (value) => setFilters(value);

  const weeks = useMemo(() => {
    if (!startedAt || !timezone) return [];

    const startOfFirstWeek = moment.tz(startedAt, timezone).startOf("week");
    const now = moment.tz(timezone).endOf("week");
    const weeksCount = Math.ceil(now.diff(startOfFirstWeek, "weeks", true));

    const weekArray = [];

    for (let i = 0; i < weeksCount; i++) {
      const startDate = startOfFirstWeek.clone().add(i, "weeks");
      const endDate = startDate.clone().endOf("week");
      weekArray.unshift({
        weekIndex: i,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
    }

    return weekArray;
  }, [startedAt, timezone]);

  const openTask = (item) => {
    setTask(item);
    setIsOpenDrawer(true);
  };

  // String ids — antd menu keys must be strings; a numeric 0 (Week 1) is a
  // known footgun in rc-menu and made the Week filter unselectable.
  const weekOptions = weeks.map(week => ({
    id: String(week.weekIndex),
    name: 'Week ' + (week.weekIndex + 1)
  }));

  const statusesList = [
    { name: "Complete", id: 'COMPLETED'},
    { name: "Dismissed", id: 'DISSMISED'},
    { name: "Pending", id: 'PENDING'},
    { name: "Incomplete", id: 'INCOMPLETED'},
  ]

  const taskTypesList = [
    { name: "Planned", id: 'PLANNED' },
    { name: "Triggered", id: 'TRIGGERED' },
    { name: "Self-assigned", id: 'SELF_ASSIGNED' },
    { name: "User-assigned", id: 'USER_ASSIGNED' },
  ]

  const getTaskList = () => {
    ApiTaskManagement.getSimpleTaskList().then(
      (r) => {
        setTaskList(r.data.dailyTasks.map( x => ({ id: x.id, name: x.title }) ));
      }
    );
  };

  useEffect(() => {
    getTaskList();
  }, []);

  const closeTaskDrawer = () => setIsOpenDrawer(false);

  const handleTaskRemoved = () => {
    setIsOpenDrawer(false);
    triggerRefresh();
  };

  const handleTaskUpdated = () => {
    setIsOpenDrawer(false);
    triggerRefresh();
  };

  return (
    <Body
      header={{
        title: "Plan",
        // Order: Preview (secondary) on the left, Assign task (primary) on the
        // right. Both go through `apply` so we control the left→right order.
        apply: [
          {
            key: "preview-future",
            label: "Preview future tasks",
            type: "default",
            testID: "plan-preview-future",
            onClick: () => setPreviewModalOpen(true),
          },
          ...(canAssign
            ? [{
              key: "assign-task",
              label: "+ Assign task",
              type: "primary",
              testID: "plan-assign-task",
              onClick: () => setIsAssignOpen(true),
            }]
            : []),
        ],
      }}
      scroll
    >
      <div className="space-y-4">
        {canSeeUpcoming && (
          <UpcomingAssignments
            subjectId={subjectId}
            timezone={timezone}
            refreshTick={refreshTick}
            onOpenTask={openTask}
          />
        )}

        <div style={{ marginBottom: 20 }}>
          <DropdownFilters
            filtersBy={[
              { name: "Week", id: "weekIndex", children: weekOptions },
              { name: "Status", id: "status", children: statusesList },
              { name: "Task type", id: "taskType", children: taskTypesList },
              { name: "Task name", id: "taskIds", children: taskList },
            ]}
            updateFilterValue={updateFilterValue}
          />
        </div>

        {(Array.isArray(filters.weekIndex) && filters.weekIndex.length > 0
          ? weeks.filter((week) => filters.weekIndex.includes(String(week.weekIndex)))
          : weeks
        ).map((week) => (
          <WeekPlan
            key={`${week.weekIndex}-${(filters.status || []).join(',')}-${(filters.taskType || []).join(',')}-${(filters.taskIds || []).join(',')}-${refreshTick}`}
            weekIndex={week.weekIndex}
            startDate={week.startDate}
            endDate={week.endDate}
            subjectId={subjectId}
            timezone={timezone}
            openTask={openTask}
            filters={filters}
          />
        ))}
      </div>
      <AssignedTaskDrawer
        open={isOpenDrawer}
        onClose={closeTaskDrawer}
        task={task}
        subjectId={subjectId}
        timezone={timezone}
        preview={!!task?.preview}
        onRemoved={handleTaskRemoved}
        onUpdated={handleTaskUpdated}
      />
      <AssignTaskDrawer
        open={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        subjectId={subjectId}
        timezone={timezone}
        onAssigned={triggerRefresh}
      />

      <ModalWrapper
        isOpen={previewModalOpen}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <span>Preview future tasks</span>
            <DatePicker
              picker="month"
              allowClear={false}
              format="MMMM YYYY"
              value={previewMonth}
              disabledDate={disabledPreviewMonth}
              onChange={(value) => value && setPreviewMonth(value.startOf("month"))}
            />
          </div>
        }
        width={1100}
        onCancel={() => setPreviewModalOpen(false)}
        footer={null}
      >
        <Divider style={{ margin: "20px 0" }} />
        <div style={{ maxHeight: "65vh", overflowY: "auto" }} className="space-y-4" data-testid="plan-preview">
          {previewWeeks.map((week) => (
            <WeekPlan
              key={`preview-${week.key}`}
              preview
              weekIndex={week.weekIndex}
              startDate={week.startDate}
              endDate={week.endDate}
              subjectId={subjectId}
              timezone={timezone}
              openTask={openTask}
              filters={filters}
            />
          ))}
        </div>
      </ModalWrapper>
    </Body>
  );
};

export default Plan;
